import React, { useState } from "react";
import { Param, ParamTable } from "./Paramtable";
import { EndpointHeader } from "./Endpointheader";
import { CodeBlock } from "./Codeblock";
import { MethodBadge } from "./Methodbadge";

type HeaderType = "text" | "image" | "video" | "document";
type TemplateMode = "simple" | "variable";
type RecipientType = "customerIds" | "mobileNumbers" | "csv";

// ─── Params ────────────────────────────────────────────────────────────────

const BASE_PARAMS: Param[] = [
  { name: "templateName", type: "string", required: true, description: "Approved template name." },
  { name: "language", type: "string", required: true, description: 'Language code, e.g. "en_US".' },
  { name: "headerType", type: "string", required: true, description: "TEXT, IMAGE, VIDEO, or DOCUMENT." },
  { name: "bodyParams", type: "array", required: false, description: "Values for body variables {{1}}, {{2}}, etc. (required for variable templates)." },
];

const RECIPIENT_PARAMS: Record<RecipientType, Param[]> = {
  customerIds: [
    { name: "customerIds", type: "array", required: true, description: "Array of customer IDs (sent as repeated form fields)." },
  ],
  mobileNumbers: [
    { name: "mobileNumbers", type: "array", required: true, description: "Array of phone numbers in E.164 format (sent as repeated form fields)." },
  ],
  csv: [
    { name: "file", type: "file", required: true, description: "CSV or Excel file containing phone numbers or customer IDs." },
  ],
};

const MEDIA_PARAMS: Param[] = [
  { name: "headerMediaId", type: "string", required: false, description: "Public media URL from /api/file-upload (required for IMAGE/VIDEO/DOCUMENT)." },
];

// ─── Media Upload ───────────────────────────────────────────────────────────

const MEDIA_UPLOAD_CURL = `curl 'https://empedance.com/api/file-upload' \\
  -H 'Authorization: Bearer <your_token>' \\
  -F 'file=@/path/to/your/file'`;

const MEDIA_UPLOAD_RESPONSE = `{
  "message": "File uploaded successfully",
  "file": "https://empedance.sgp1.digitaloceanspaces.com/Whatsaap/your-file.png"
}`;

// ─── Payload & cURL Builders ────────────────────────────────────────────────

function buildPayload(
  headerType: HeaderType,
  mode: TemplateMode,
  recipientType: RecipientType
): string {
  const isVariable = mode === "variable";
  const isMedia = headerType !== "text";

  return `// multipart/form-data request
{
  "templateName": "${isVariable ? "ippo_otp" : "marketing_text_simple"}",
  "language": "en_US",
  "headerType": "${headerType.toUpperCase()}",
  "bodyParams": ${isVariable ? '["1234"]' : "[]"},
  ${isMedia ? `"headerMediaId": "https://empedance.sgp1.digitaloceanspaces.com/Whatsaap/your-file.png",` : ""}
  ${recipientType === "customerIds" ? `"customerIds": [108, 2]` : ""}
  ${recipientType === "mobileNumbers" ? `"mobileNumbers": ["919113887558", "917351166989"]` : ""}
  ${recipientType === "csv" ? `"file": "<CSV/Excel file>"` : ""}
}`;
}

function buildCurl(
  headerType: HeaderType,
  mode: TemplateMode,
  recipientType: RecipientType
): string {
  const isVariable = mode === "variable";
  const isMedia = headerType !== "text";

  const headerField = isMedia
    ? `  -F 'headerMediaId=https://empedance.sgp1.digitaloceanspaces.com/Whatsaap/your-file.png' \\\n`
    : "";

  const bodyParamsField = isVariable
    ? `  -F 'bodyParams[]=1234' \\\n`
    : "";

  const recipientFields: Record<RecipientType, string> = {
    customerIds: `  -F 'customerIds=108' \\\n  -F 'customerIds=2'`,
    mobileNumbers: `  -F 'mobileNumbers=919113887558' \\\n  -F 'mobileNumbers=917351166989'`,
    csv: `  -F 'file=@/path/to/contacts.csv'`,
  };

  return `curl 'https://apiwhat.empedance.com/whatsapp/template/bulk' \\
  -H 'Authorization: Bearer <your_token>' \\
  -H 'Content-Type: multipart/form-data' \\
  -F 'templateName=${isVariable ? "ippo_otp" : "marketing_text_simple"}' \\
  -F 'language=en_US' \\
  -F 'headerType=${headerType.toUpperCase()}' \\
${bodyParamsField}${headerField}${recipientFields[recipientType]}`;
}

// ─── Response ───────────────────────────────────────────────────────────────

const RESPONSE_SAMPLE = `{
  "status": 1,
  "data": "Bulk WhatsApp messages queued successfully",
  "error": null
}`;

// ─── Toggle Pill ────────────────────────────────────────────────────────────

function TogglePill<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { id: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        {label}
      </p>
      <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {options.map(({ id, label: optLabel }) => (
          <button
            key={id}
            onClick={() => onChange(id)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-150 ${value === id
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
              }`}
          >
            {optLabel}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Options ────────────────────────────────────────────────────────────────

const HEADER_OPTIONS: { id: HeaderType; label: string }[] = [
  { id: "text", label: "Text" },
  { id: "image", label: "Image" },
  { id: "video", label: "Video" },
  { id: "document", label: "Document" },
];

const MODE_OPTIONS: { id: TemplateMode; label: string }[] = [
  { id: "simple", label: "Simple" },
  { id: "variable", label: "Variable" },
];

const RECIPIENT_OPTIONS: { id: RecipientType; label: string }[] = [
  { id: "customerIds", label: "Customer IDs" },
  { id: "mobileNumbers", label: "Mobile Numbers" },
  { id: "csv", label: "CSV / Excel File" },
];

// ─── Main Component ─────────────────────────────────────────────────────────

export const SendBulkSection: React.FC = () => {
  const [headerType, setHeaderType] = useState<HeaderType>("text");
  const [mode, setMode] = useState<TemplateMode>("simple");
  const [recipientType, setRecipientType] = useState<RecipientType>("customerIds");

  const isMediaType = headerType !== "text";
  const isVariable = mode === "variable";

  const allParams: Param[] = [
    ...BASE_PARAMS,
    ...(isMediaType ? MEDIA_PARAMS : []),
    ...RECIPIENT_PARAMS[recipientType],
  ];

  return (
    <div>
      {/* Title */}
      <div className="flex items-center gap-3 mb-2">
        <MethodBadge method="POST" />
        <h1 className="text-2xl font-bold text-gray-900">Send Bulk Template Message</h1>
      </div>

      <p className="text-gray-500 text-sm mt-1 mb-6">
        Send an approved WhatsApp template to multiple recipients using customer IDs, mobile numbers, or CSV/Excel file.
      </p>

      <EndpointHeader method="POST" url="/whatsapp/template/bulk" />

      {/* Toggles */}
      <div className="mt-5 flex flex-wrap gap-6">
        <TogglePill
          label="Header Type"
          options={HEADER_OPTIONS}
          value={headerType}
          onChange={setHeaderType}
        />

        <TogglePill
          label="Template Mode"
          options={MODE_OPTIONS}
          value={mode}
          onChange={setMode}
        />

        <TogglePill
          label="Send To"
          options={RECIPIENT_OPTIONS}
          value={recipientType}
          onChange={setRecipientType}
        />
      </div>

      {/* Variable Explainer */}
      {isVariable && (
        <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-xl">
          <p className="text-sm font-semibold text-indigo-800 mb-1">
            🔢 Variable Template
          </p>
          <p className="text-xs text-indigo-700">
            Provide values for placeholders like <code className="bg-indigo-100 px-1 rounded">{"{{1}}"}</code> using{" "}
            <code>bodyParams</code>.
          </p>
          <p className="text-xs text-indigo-700 mt-2">
            Example: For template containing{" "}
            <code>{'Your OTP is {{1}}'}</code>, use{" "}
            <code>{'bodyParams[]=1234'}</code> in form data.
          </p>
        </div>
      )}

      {/* Media Upload */}
      {isMediaType && (
        <div className="mt-5 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-sm font-semibold text-blue-800 mb-1">
            📎 Step 1 — Upload your {headerType} first
          </p>
          <p className="text-xs text-blue-700 mb-3">
            Upload the file and use the returned URL as <code>headerMediaId</code>.
          </p>
          <CodeBlock
            title="POST https://empedance.com/api/file-upload"
            variants={[{ label: "cURL", value: MEDIA_UPLOAD_CURL }]}
          />
          <CodeBlock
            title="RESPONSE 200 · OK"
            variants={[{ label: "JSON", value: MEDIA_UPLOAD_RESPONSE }]}
          />
        </div>
      )}

      {/* CSV Note */}
      {recipientType === "csv" && (
        <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-600">
          <span className="font-semibold text-gray-700">CSV / Excel Format:</span>
          The file should contain one phone number (E.164) or customer ID per row. No header row needed.
        </div>
      )}

      {/* Params Table */}
      <ParamTable title="Request Body (multipart/form-data)" params={allParams} />

      {/* Request Example */}
      <CodeBlock
        title="Request Example"
        variants={[
          { label: "Payload", value: buildPayload(headerType, mode, recipientType) },
          { label: "cURL", value: buildCurl(headerType, mode, recipientType) },
        ]}
      />

      {/* Response */}
      <CodeBlock
        title="RESPONSE 200 · OK"
        variants={[{ label: "JSON", value: RESPONSE_SAMPLE }]}
      />
    </div>
  );
};